import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Chip,
} from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

const MEMBER_TYPES = ["INTERNAL", "EXTERNAL", "STUDENT", "PARENT", "OTHER"];

const UserCollegeRoleBulkAssignment = () => {
  // ============================================================
  // SESSION USER
  // ============================================================

  const user = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("user") || "{}");
    } catch (error) {
      console.error("Invalid sessionStorage user:", error);
      return {};
    }
  }, []);

  const clientId = user?.client_id;

  const selectedNavbarCollegeId = Number(user?.college_id);

  const loggedUserId = Number(user?.user_id || sessionStorage.getItem("user_id")) || null;

  // ============================================================
  // MEMBER TYPE
  // ============================================================

  const [memberType, setMemberType] = useState("");

  // ============================================================
  // STUDENT ADMITTED BATCH
  // ============================================================

  const [admittedBatches, setAdmittedBatches] = useState([]);
  const [admittedBatchId, setAdmittedBatchId] = useState("");

  const [loadingAdmittedBatches, setLoadingAdmittedBatches] = useState(false);

  // ============================================================
  // USERS
  // ============================================================

  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const [userSearch, setUserSearch] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);

  // ============================================================
  // COLLEGES
  // ============================================================

  const [colleges, setColleges] = useState([]);
  const [selectedCollegeIds, setSelectedCollegeIds] = useState([]);

  const [loadingColleges, setLoadingColleges] = useState(false);

  // ============================================================
  // ROLES
  // ============================================================

  const [roles, setRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);

  const [loadingRoles, setLoadingRoles] = useState(false);

  // ============================================================
  // SAVE
  // ============================================================

  const [saving, setSaving] = useState(false);
  const selectedItemSx = (selected) => ({
    mb: 1,
    borderRadius: 1.5,
    border: "1px solid",
    borderColor: selected ? "primary.main" : "divider",
    backgroundColor: selected ? "rgba(25, 118, 210, 0.08)" : "background.paper",
    transition: "all 0.2s ease",
  });

  const countChipSx = {
    fontWeight: 600,
  };
  // ============================================================
  // SNACKBAR
  // ============================================================

  const [notice, setNotice] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  // ============================================================
  // STUDENT ROLE CHECK
  // ============================================================

  const isStudentRole = (role) => {
    return (
      String(role?.role_code || "").toUpperCase() === "STUDENT" ||
      String(role?.role_name || "").toUpperCase() === "STUDENT"
    );
  };

  // ============================================================
  // SHOW NOTICE
  // ============================================================

  const showNotice = (severity, message) => {
    setNotice({
      open: true,
      severity,
      message,
    });
  };

  // ============================================================
  // LOAD COLLEGES
  // ============================================================

  const loadColleges = async () => {
    if (!clientId) return;

    try {
      setLoadingColleges(true);

      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/bulk-assignment/colleges/${clientId}`
      );

      if (response.data?.success) {
        setColleges(response.data.data || []);
      } else {
        setColleges([]);
      }
    } catch (error) {
      console.error("loadColleges error:", error.response?.data || error);

      setColleges([]);

      showNotice("error", error.response?.data?.message || "Failed to load colleges");
    } finally {
      setLoadingColleges(false);
    }
  };

  // ============================================================
  // LOAD ADMITTED BATCHES
  // ============================================================

  const loadAdmittedBatches = async (collegeId = selectedNavbarCollegeId) => {
    if (!clientId || !collegeId || Number(collegeId) <= 0) {
      setAdmittedBatches([]);
      return;
    }

    try {
      setLoadingAdmittedBatches(true);

      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/bulk-assignment/admitted-batches/${clientId}/${Number(
          collegeId
        )}`
      );

      if (response.data?.success) {
        setAdmittedBatches(response.data.data || []);
      } else {
        setAdmittedBatches([]);
      }
    } catch (error) {
      console.error("loadAdmittedBatches error:", error.response?.data || error);

      setAdmittedBatches([]);

      showNotice("error", error.response?.data?.message || "Failed to load admitted batches");
    } finally {
      setLoadingAdmittedBatches(false);
    }
  };

  // ============================================================
  // LOAD USERS
  //
  // INTERNAL:
  //   member_type=INTERNAL
  //   college_id=<Navbar college>
  //
  // EXTERNAL:
  //   member_type=EXTERNAL
  //   NO college_id
  //
  // STUDENT:
  //   member_type=STUDENT
  //   college_id=<Navbar college>
  //   admittedbatch_id=<selected batch>
  //
  // ============================================================

  const loadUsers = async ({
    memberTypeOverride = memberType,
    collegeIdOverride = selectedNavbarCollegeId,
    admittedBatchIdOverride = admittedBatchId,
    searchOverride = userSearch,
  } = {}) => {
    if (!clientId || !memberTypeOverride) {
      return;
    }

    // ------------------------------------------------------------
    // STUDENT
    // Do not load anything until batch is selected
    // ------------------------------------------------------------

    if (memberTypeOverride === "STUDENT" && !admittedBatchIdOverride) {
      setUsers([]);
      return;
    }

    // ------------------------------------------------------------
    // INTERNAL
    // College is mandatory
    // ------------------------------------------------------------

    if (
      memberTypeOverride === "INTERNAL" &&
      (!collegeIdOverride || Number(collegeIdOverride) <= 0)
    ) {
      console.log("Internal users API skipped: college_id missing");

      setUsers([]);

      return;
    }

    // ------------------------------------------------------------
    // STUDENT
    // College is mandatory
    // ------------------------------------------------------------

    if (
      memberTypeOverride === "STUDENT" &&
      (!collegeIdOverride || Number(collegeIdOverride) <= 0)
    ) {
      console.log("Student users API skipped: college_id missing");

      setUsers([]);

      return;
    }

    try {
      setLoadingUsers(true);

      const params = {
        member_type: memberTypeOverride,
        search: searchOverride || "",
      };

      // ----------------------------------------------------------
      // INTERNAL
      // ----------------------------------------------------------

      if (memberTypeOverride === "INTERNAL") {
        params.college_id = Number(collegeIdOverride);
      }

      // ----------------------------------------------------------
      // STUDENT
      // ----------------------------------------------------------

      if (memberTypeOverride === "STUDENT") {
        params.college_id = Number(collegeIdOverride);

        params.admittedbatch_id = Number(admittedBatchIdOverride);
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/bulk-assignment/users/${clientId}`,
        {
          params,
        }
      );

      if (response.data?.success) {
        const data = response.data.data || [];

        setUsers(data);

        // Preserve already selected users.
        // Also automatically select already assigned users
        // only when they have not already been selected.
        setSelectedUserIds((previous) => {
          const assignedIds = data
            .filter((item) => item.is_assigned)
            .map((item) => Number(item.user_id));

          const previousIds = previous.map(Number);

          return Array.from(new Set([...previousIds, ...assignedIds]));
        });
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("loadUsers error:", error.response?.data || error);

      setUsers([]);

      showNotice("error", error.response?.data?.message || "Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  // ============================================================
  // LOAD ROLES
  // ============================================================

  const loadRoles = async () => {
    if (!clientId || !selectedCollegeIds.length) {
      setRoles([]);
      return;
    }

    const collegeId = Number(selectedCollegeIds[0]);

    if (!collegeId || collegeId <= 0) {
      setRoles([]);
      return;
    }

    try {
      setLoadingRoles(true);

      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/bulk-assignment/roles/${clientId}/${collegeId}`
      );

      if (response.data?.success) {
        const data = response.data.data || [];

        setRoles(data);

        // --------------------------------------------------------
        // STUDENT
        // Automatically select Student role only
        // --------------------------------------------------------

        if (memberType === "STUDENT") {
          const studentRole = data.find(isStudentRole);

          if (studentRole) {
            setSelectedRoleIds([Number(studentRole.role_id)]);
          } else {
            setSelectedRoleIds([]);
          }
        }
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error("loadRoles error:", error.response?.data || error);

      setRoles([]);

      showNotice("error", error.response?.data?.message || "Failed to load roles");
    } finally {
      setLoadingRoles(false);
    }
  };

  // ============================================================
  // INITIAL LOAD
  // ============================================================

  useEffect(() => {
    loadColleges();
  }, [clientId]);

  // ============================================================
  // MEMBER TYPE CHANGE
  // ============================================================

  const handleMemberTypeChange = async (event) => {
    const value = event.target.value;

    setMemberType(value);

    // Clear old selections/data
    setUsers([]);
    setSelectedUserIds([]);

    setSelectedCollegeIds([]);
    setSelectedRoleIds([]);

    setRoles([]);

    setUserSearch("");

    setAdmittedBatches([]);
    setAdmittedBatchId("");

    if (!value) {
      return;
    }

    // ==========================================================
    // STUDENT
    // ==========================================================

    if (value === "STUDENT") {
      if (Number.isFinite(selectedNavbarCollegeId) && selectedNavbarCollegeId > 0) {
        // Student college is fixed to Navbar college
        setSelectedCollegeIds([selectedNavbarCollegeId]);

        // Load batches only.
        // DO NOT load students here.
        await loadAdmittedBatches(selectedNavbarCollegeId);
      }

      return;
    }

    // ==========================================================
    // INTERNAL
    // ==========================================================

    if (value === "INTERNAL") {
      if (Number.isFinite(selectedNavbarCollegeId) && selectedNavbarCollegeId > 0) {
        // Internal users are filtered by Navbar college
        await loadUsers({
          memberTypeOverride: "INTERNAL",
          collegeIdOverride: selectedNavbarCollegeId,
        });
      } else {
        showNotice("warning", "No college is selected in the Navbar.");
      }

      return;
    }

    // ==========================================================
    // EXTERNAL / PARENT / OTHER
    // ==========================================================

    await loadUsers({
      memberTypeOverride: value,
    });
  };

  // ============================================================
  // STUDENT BATCH CHANGE
  // ============================================================

  const handleAdmittedBatchChange = async (event) => {
    const batchId = event.target.value;

    setAdmittedBatchId(batchId);

    // Clear old student users
    setUsers([]);
    setSelectedUserIds([]);

    if (!batchId) {
      return;
    }

    // Load students only now
    await loadUsers({
      memberTypeOverride: "STUDENT",
      collegeIdOverride: selectedNavbarCollegeId,
      admittedBatchIdOverride: batchId,
    });
  };

  // ============================================================
  // NAVBAR COLLEGE → STUDENT
  // ============================================================

  useEffect(() => {
    if (
      memberType === "STUDENT" &&
      Number.isFinite(selectedNavbarCollegeId) &&
      selectedNavbarCollegeId > 0
    ) {
      setSelectedCollegeIds([selectedNavbarCollegeId]);

      // If the batch is already selected, reload students
      if (admittedBatchId) {
        loadUsers({
          memberTypeOverride: "STUDENT",
          collegeIdOverride: selectedNavbarCollegeId,
          admittedBatchIdOverride: admittedBatchId,
        });
      }
    }
  }, [memberType, selectedNavbarCollegeId]);

  // ============================================================
  // LOAD ROLES WHEN COLLEGE CHANGES
  // ============================================================

  useEffect(() => {
    if (!selectedCollegeIds.length) {
      setRoles([]);
      setSelectedRoleIds([]);
      return;
    }

    loadRoles();
  }, [selectedCollegeIds, memberType]);

  // ============================================================
  // USER CHECKBOX
  // ============================================================

  const handleUserChange = (userId) => {
    const id = Number(userId);

    setSelectedUserIds((previous) => {
      if (previous.includes(id)) {
        return previous.filter((item) => item !== id);
      }

      return [...previous, id];
    });
  };

  // ============================================================
  // SELECT ALL USERS
  // ============================================================

  const allUsersSelected =
    users.length > 0 && users.every((item) => selectedUserIds.includes(Number(item.user_id)));

  const handleSelectAllUsers = () => {
    const visibleIds = users.map((item) => Number(item.user_id));

    if (allUsersSelected) {
      setSelectedUserIds((previous) => previous.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedUserIds((previous) => Array.from(new Set([...previous, ...visibleIds])));
    }
  };

  // ============================================================
  // COLLEGE CHECKBOX
  // ============================================================

  const handleCollegeChange = (collegeId) => {
    const id = Number(collegeId);

    // Student college cannot be changed
    if (memberType === "STUDENT") {
      if (id === selectedNavbarCollegeId) {
        setSelectedCollegeIds([selectedNavbarCollegeId]);
      }

      return;
    }

    setSelectedCollegeIds((previous) => {
      if (previous.includes(id)) {
        return previous.filter((item) => item !== id);
      }

      return [...previous, id];
    });
  };

  // ============================================================
  // SELECT ALL COLLEGES
  // ============================================================

  const allCollegesSelected =
    memberType !== "STUDENT" &&
    colleges.length > 0 &&
    colleges.every((college) => selectedCollegeIds.includes(Number(college.college_id)));

  const handleSelectAllColleges = () => {
    if (memberType === "STUDENT") {
      return;
    }

    if (allCollegesSelected) {
      setSelectedCollegeIds([]);
    } else {
      setSelectedCollegeIds(colleges.map((college) => Number(college.college_id)));
    }
  };

  // ============================================================
  // ROLE CHECKBOX
  // ============================================================

  const handleRoleChange = (role) => {
    const id = Number(role.role_id);

    // Student can select ONLY Student role
    if (memberType === "STUDENT") {
      if (isStudentRole(role)) {
        setSelectedRoleIds([id]);
      }

      return;
    }

    setSelectedRoleIds((previous) => {
      if (previous.includes(id)) {
        return previous.filter((item) => item !== id);
      }

      return [...previous, id];
    });
  };

  // ============================================================
  // SELECT ALL ROLES
  // ============================================================

  const allRolesSelected =
    memberType !== "STUDENT" &&
    roles.length > 0 &&
    roles.every((role) => selectedRoleIds.includes(Number(role.role_id)));

  const handleSelectAllRoles = () => {
    if (memberType === "STUDENT") {
      return;
    }

    if (allRolesSelected) {
      setSelectedRoleIds([]);
    } else {
      setSelectedRoleIds(roles.map((role) => Number(role.role_id)));
    }
  };

  // ============================================================
  // SEARCH
  // ============================================================

  const searchTimerRef = useRef(null);

  const handleSearch = (event) => {
    const value = event.target.value;

    setUserSearch(value);

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      if (memberType === "STUDENT" && !admittedBatchId) return;

      if (
        memberType === "INTERNAL" &&
        (!selectedNavbarCollegeId || Number(selectedNavbarCollegeId) <= 0)
      ) {
        return;
      }

      loadUsers({
        memberTypeOverride: memberType,
        collegeIdOverride: selectedNavbarCollegeId,
        admittedBatchIdOverride: admittedBatchId,
        searchOverride: value,
      });
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  // ============================================================
  // REFRESH
  // ============================================================

  const handleRefresh = async () => {
    if (!clientId) return;

    setUsers([]);
    setSelectedUserIds([]);

    await loadColleges();

    // ----------------------------------------------------------
    // STUDENT
    // ----------------------------------------------------------

    if (memberType === "STUDENT") {
      setAdmittedBatches([]);

      if (selectedNavbarCollegeId && Number(selectedNavbarCollegeId) > 0) {
        await loadAdmittedBatches(selectedNavbarCollegeId);
      }

      if (admittedBatchId) {
        await loadUsers({
          memberTypeOverride: "STUDENT",
          collegeIdOverride: selectedNavbarCollegeId,
          admittedBatchIdOverride: admittedBatchId,
          searchOverride: userSearch,
        });
      }

      if (selectedCollegeIds.length) {
        await loadRoles();
      }

      return;
    }

    // ----------------------------------------------------------
    // INTERNAL
    // ----------------------------------------------------------

    if (memberType === "INTERNAL") {
      if (selectedNavbarCollegeId && Number(selectedNavbarCollegeId) > 0) {
        await loadUsers({
          memberTypeOverride: "INTERNAL",
          collegeIdOverride: selectedNavbarCollegeId,
          searchOverride: userSearch,
        });
      }

      if (selectedCollegeIds.length) {
        await loadRoles();
      }

      return;
    }

    // ----------------------------------------------------------
    // EXTERNAL / PARENT / OTHER
    // ----------------------------------------------------------

    if (memberType) {
      await loadUsers({
        memberTypeOverride: memberType,
        searchOverride: userSearch,
      });
    }

    if (selectedCollegeIds.length) {
      await loadRoles();
    }
  };

  // ============================================================
  // SAVE
  // ============================================================

  const handleSave = async () => {
    if (!memberType) {
      showNotice("warning", "Please select member type.");
      return;
    }

    if (!selectedUserIds.length) {
      showNotice("warning", "Please select at least one user.");
      return;
    }

    if (!selectedCollegeIds.length) {
      showNotice("warning", "Please select at least one college.");
      return;
    }

    if (!selectedRoleIds.length) {
      showNotice("warning", "Please select at least one role.");
      return;
    }

    // ----------------------------------------------------------
    // STUDENT VALIDATION
    // ----------------------------------------------------------

    if (memberType === "STUDENT") {
      if (!admittedBatchId) {
        showNotice("warning", "Please select admitted batch.");
        return;
      }

      if (
        selectedCollegeIds.length !== 1 ||
        Number(selectedCollegeIds[0]) !== Number(selectedNavbarCollegeId)
      ) {
        showNotice("warning", "Student college must be the college selected in the Navbar.");
        return;
      }

      const invalidRole = selectedRoleIds.some((roleId) => {
        const role = roles.find((item) => Number(item.role_id) === Number(roleId));

        return role && !isStudentRole(role);
      });

      if (invalidRole) {
        showNotice("warning", "Only the Student role can be assigned to students.");
        return;
      }
    }

    try {
      setSaving(true);

      const payload = {
        member_type: memberType,

        user_ids: selectedUserIds.map(Number),

        college_ids: selectedCollegeIds.map(Number),

        role_ids: selectedRoleIds.map(Number),

        modified_by: loggedUserId,

        admittedbatch_id: admittedBatchId ? Number(admittedBatchId) : null,
      };

      const response = await axios.put(
        `${API_BASE_URL}/api/rbacall/bulk-assignment/${clientId}`,
        payload
      );

      if (response.data?.success) {
        showNotice(
          "success",
          response.data.message || "User college and role assignment saved successfully."
        );

        // Reload users so assignment status is refreshed
        if (memberType === "STUDENT") {
          if (admittedBatchId) {
            await loadUsers({
              memberTypeOverride: "STUDENT",
              collegeIdOverride: selectedNavbarCollegeId,
              admittedBatchIdOverride: admittedBatchId,
              searchOverride: userSearch,
            });
          }
        } else if (memberType === "INTERNAL") {
          await loadUsers({
            memberTypeOverride: "INTERNAL",
            collegeIdOverride: selectedNavbarCollegeId,
            searchOverride: userSearch,
          });
        } else {
          await loadUsers({
            memberTypeOverride: memberType,
            searchOverride: userSearch,
          });
        }

        await loadRoles();
      } else {
        showNotice("error", response.data?.message || "Failed to save assignment.");
      }
    } catch (error) {
      console.error("handleSave error:", error.response?.data || error);

      showNotice("error", error.response?.data?.message || "Failed to save assignment.");
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // SELECTED USER COUNT
  // ============================================================

  const selectedUserCount = selectedUserIds.length;

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <Box sx={{ p: 2 }}>
      <Paper
        elevation={3}
        sx={{
          p: 2,
          borderRadius: 2,
        }}
      >
        {/* ======================================================
            TITLE
        ======================================================= */}

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              User College & Role Assignment
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Assign users to colleges and roles
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* ======================================================
            MEMBER TYPE
        ======================================================= */}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            select
            label="Member Type"
            value={memberType}
            onChange={handleMemberTypeChange}
            InputLabelProps={{
              shrink: true,
            }}
            SelectProps={{
              native: true,
            }}
            disabled={saving}
            sx={{
              width: {
                xs: "100%",
                sm: 250,
              },
            }}
          >
            <option value="">Select Member Type</option>

            {MEMBER_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </TextField>

          {memberType === "STUDENT" && (
            <TextField
              select
              label="Admitted Batch"
              value={admittedBatchId}
              onChange={handleAdmittedBatchChange}
              InputLabelProps={{
                shrink: true,
              }}
              SelectProps={{
                native: true,
              }}
              disabled={loadingAdmittedBatches || saving}
              sx={{
                width: {
                  xs: "100%",
                  sm: 280,
                },
              }}
            >
              <option value="">Select Admitted Batch</option>

              {admittedBatches.map((batch) => (
                <option key={batch.admittedbatch_id} value={batch.admittedbatch_id}>
                  {batch.admittedbatch}
                </option>
              ))}
            </TextField>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={
              loadingUsers || loadingColleges || loadingRoles || loadingAdmittedBatches || saving
            }
            sx={{
              minWidth: 170,
              backgroundColor: "#1976d2",
              color: "#ffffff",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 1.5,

              "&:hover": {
                backgroundColor: "#1565c0",
              },

              "&.Mui-disabled": {
                backgroundColor: "#90caf9",
                color: "#ffffff",
              },
            }}
          >
            Refresh
          </Button>
        </Stack>

        {/* ======================================================
            USERS
        ======================================================= */}

        {memberType && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h6" fontWeight="bold">
                  Users
                </Typography>

                <Chip size="small" label={`Fetched: ${users.length}`} sx={countChipSx} />

                <Chip
                  size="small"
                  label={`Selected: ${selectedUserIds.length}`}
                  color={selectedUserIds.length > 0 ? "primary" : "default"}
                  sx={countChipSx}
                />
              </Stack>

              <TextField
                size="small"
                label="Search User"
                value={userSearch}
                onChange={handleSearch}
                disabled={loadingUsers || saving || (memberType === "STUDENT" && !admittedBatchId)}
                placeholder={
                  memberType === "STUDENT" && !admittedBatchId
                    ? "Select admitted batch first"
                    : "Name, email or mobile"
                }
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    fontSize: "1rem",
                    fontWeight: 600,
                  },
                }}
                sx={{
                  width: { xs: "100%", sm: 320 },

                  "& .MuiInputBase-input": {
                    fontSize: "1.05rem",
                    fontWeight: 500,
                    padding: "10px 12px",
                  },

                  "& .MuiOutlinedInput-root": {
                    minHeight: 48,
                  },

                  "& .MuiFormHelperText-root": {
                    fontSize: "0.9rem",
                  },
                }}
              />
            </Stack>

            {/* Student instruction */}

            {memberType === "STUDENT" && !admittedBatchId && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Please select an admitted batch to load students.
              </Alert>
            )}

            {/* Select all */}

            {users.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allUsersSelected}
                      onChange={handleSelectAllUsers}
                      disabled={loadingUsers || saving}
                    />
                  }
                  label="Select All Users"
                />
              </Box>
            )}

            <Divider sx={{ mb: 1 }} />

            {/* Loading */}

            {loadingUsers && (
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                spacing={1}
                sx={{ py: 4 }}
              >
                <CircularProgress size={28} />

                <Typography>Loading users...</Typography>
              </Stack>
            )}

            {/* No users */}

            {!loadingUsers && memberType !== "STUDENT" && users.length === 0 && (
              <Typography color="text.secondary" sx={{ py: 3 }} textAlign="center">
                No users found.
              </Typography>
            )}

            {!loadingUsers && memberType === "STUDENT" && admittedBatchId && users.length === 0 && (
              <Typography color="text.secondary" sx={{ py: 3 }} textAlign="center">
                No students found for the selected admitted batch.
              </Typography>
            )}

            {/* User list */}

            {!loadingUsers && users.length > 0 && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                {users.map((item) => {
                  const userId = Number(item.user_id);

                  const checked = selectedUserIds.includes(userId);

                  return (
                    <Paper
                      key={userId}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: checked ? "primary.main" : "divider",
                        backgroundColor: checked ? "rgba(25, 118, 210, 0.08)" : "background.paper",
                        transition: "all 0.2s ease",

                        "&:hover": {
                          borderColor: checked ? "primary.main" : "text.secondary",
                        },
                      }}
                    >
                      <FormControlLabel
                        sx={{
                          width: "100%",
                          m: 0,
                          alignItems: "flex-start",
                        }}
                        control={
                          <Checkbox
                            checked={checked}
                            onChange={() => handleUserChange(userId)}
                            disabled={saving}
                            color="primary"
                          />
                        }
                        label={
                          <Box sx={{ pt: 0.4 }}>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Typography
                                fontWeight={checked ? "bold" : "normal"}
                                color={checked ? "primary.main" : "text.primary"}
                              >
                                {item.user_name || "Unnamed User"}
                              </Typography>

                              {checked && (
                                <Chip
                                  size="small"
                                  color="primary"
                                  label="Selected"
                                  sx={{
                                    height: 21,
                                    fontSize: "0.7rem",
                                  }}
                                />
                              )}
                            </Stack>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                wordBreak: "break-word",
                              }}
                            >
                              {item.user_email || "-"}
                            </Typography>

                            {item.user_mobile && (
                              <Typography variant="body2" color="text.secondary">
                                {item.user_mobile}
                              </Typography>
                            )}
                          </Box>
                        }
                      />

                      {/* Existing assignments */}
                      {item.is_assigned && (
                        <Box
                          sx={{
                            ml: 4.5,
                            mt: 0.5,
                          }}
                        >
                          <Chip
                            size="small"
                            label="Already Assigned"
                            sx={{
                              mb: 0.75,
                              height: 22,
                            }}
                          />

                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {(item.assigned_roles || []).map((assignedRole, index) => (
                              <Chip
                                key={`${userId}-${index}`}
                                size="small"
                                variant="outlined"
                                label={`${assignedRole.college_name || "Unknown College"} - ${
                                  assignedRole.role_name || "Unknown Role"
                                }`}
                                sx={{
                                  maxWidth: "100%",
                                  height: 24,
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Paper>
        )}

        {/* ======================================================
            COLLEGES
        ======================================================= */}

        {selectedUserIds.length > 0 && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Colleges
                </Typography>

                {memberType === "STUDENT" && (
                  <Typography variant="body2" color="text.secondary">
                    Students can be assigned only to the Navbar selected college.
                  </Typography>
                )}
              </Box>

              {memberType !== "STUDENT" && colleges.length > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allCollegesSelected}
                      onChange={handleSelectAllColleges}
                      disabled={loadingColleges || saving}
                    />
                  }
                  label="Select All"
                />
              )}
            </Stack>

            <Divider sx={{ mb: 1 }} />

            {loadingColleges ? (
              <Stack direction="row" justifyContent="center" spacing={1} sx={{ py: 3 }}>
                <CircularProgress size={25} />

                <Typography>Loading colleges...</Typography>
              </Stack>
            ) : (
              <FormGroup>
                {colleges.map((college) => {
                  const collegeId = Number(college.college_id);

                  const isNavbarCollege = collegeId === Number(selectedNavbarCollegeId);

                  const isDisabled = memberType === "STUDENT" && !isNavbarCollege;

                  const checked = selectedCollegeIds.includes(collegeId);

                  return (
                    <FormControlLabel
                      key={collegeId}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={() => handleCollegeChange(collegeId)}
                          disabled={isDisabled || saving}
                        />
                      }
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography>{college.college_name}</Typography>

                          {memberType === "STUDENT" && isNavbarCollege && (
                            <Chip size="small" label="Navbar College" />
                          )}
                        </Stack>
                      }
                    />
                  );
                })}
              </FormGroup>
            )}
          </Paper>
        )}

        {/* ======================================================
            ROLES
        ======================================================= */}

        {selectedUserIds.length > 0 && selectedCollegeIds.length > 0 && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Roles
                </Typography>

                {memberType === "STUDENT" && (
                  <Typography variant="body2" color="text.secondary">
                    Only Student role can be assigned to students.
                  </Typography>
                )}
              </Box>

              {memberType !== "STUDENT" && roles.length > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allRolesSelected}
                      onChange={handleSelectAllRoles}
                      disabled={loadingRoles || saving}
                    />
                  }
                  label="Select All"
                />
              )}
            </Stack>

            <Divider sx={{ mb: 1 }} />

            {loadingRoles ? (
              <Stack direction="row" justifyContent="center" spacing={1} sx={{ py: 3 }}>
                <CircularProgress size={25} />

                <Typography>Loading roles...</Typography>
              </Stack>
            ) : roles.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" sx={{ py: 3 }}>
                No roles found.
              </Typography>
            ) : (
              <FormGroup>
                {roles.map((role) => {
                  const roleId = Number(role.role_id);

                  const studentRole = isStudentRole(role);

                  const disabled = memberType === "STUDENT" && !studentRole;

                  const checked = selectedRoleIds.includes(roleId);

                  return (
                    <FormControlLabel
                      key={roleId}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={() => handleRoleChange(role)}
                          disabled={disabled || saving}
                        />
                      }
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography>{role.role_name}</Typography>

                          {memberType === "STUDENT" && studentRole && (
                            <Chip size="small" label="Student Role" />
                          )}
                        </Stack>
                      }
                    />
                  );
                })}
              </FormGroup>
            )}
          </Paper>
        )}

        {/* ======================================================
            SAVE
        ======================================================= */}

        {selectedUserIds.length > 0 &&
          selectedCollegeIds.length > 0 &&
          selectedRoleIds.length > 0 && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={
                  saving ? <CircularProgress size={18} sx={{ color: "#ffffff" }} /> : <SaveIcon />
                }
                sx={{
                  minWidth: 170,
                  backgroundColor: "#1976d2",
                  color: "#ffffff",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: 1.5,

                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },

                  "&.Mui-disabled": {
                    backgroundColor: "#90caf9",
                    color: "#ffffff",
                  },
                }}
              >
                {saving ? "Saving..." : "Save Assignment"}
              </Button>
            </Stack>
          )}
      </Paper>

      {/* ========================================================
          SNACKBAR
      ========================================================= */}

      <Snackbar
        open={notice.open}
        autoHideDuration={5000}
        onClose={() =>
          setNotice({
            ...notice,
            open: false,
          })
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={notice.severity}
          onClose={() =>
            setNotice({
              ...notice,
              open: false,
            })
          }
          sx={{
            width: "100%",
          }}
        >
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserCollegeRoleBulkAssignment;
